import { useQuery } from '@tanstack/react-query'
import apiInstance from '@services/api/apiInstance'
import { useSnackbar } from 'notistack'
import { useOfflineMutation } from '@hooks/useOfflineMutation'
import type { Protocol } from '@services/protocols/protocols.hook'

export interface GalleryPhoto {
  id: number
  url: string
  url840490: string
  isPlan: boolean
}

export interface ProtocolHouseholdAppliance {
  id: number
  householdAppliance: number
  householdApplianceType: string
  serialNumber: string
  photos: GalleryPhoto[]
}

export interface HouseholdAppliance {
  id: number
  type: number
  serialNumber: string | null
  producer: number | null
  purchaseDate: string | null
  status: string | null
  photos?: GalleryPhoto[]
}

export const useProtocolHouseholdAppliances = (protocolId: Protocol['id']) => {
  const { enqueueSnackbar } = useSnackbar()

  const { data: protocolHouseholdAppliances = [], ...rest } = useQuery({
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    queryKey: ['protocolHouseholdAppliances', protocolId],
    queryFn: () =>
      apiInstance<ProtocolHouseholdAppliance[]>({
        url: `/admin/protocol/protocols/${protocolId}/protocol-household-appliances/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  const createProtocolHouseholdAppliance = useOfflineMutation<
    ProtocolHouseholdAppliance,
    any,
    Partial<ProtocolHouseholdAppliance>
  >({
    endpoint: `/admin/protocol/protocols/${protocolId}/protocol-household-appliances/`,
    method: 'POST',
    mutationType: 'protocolHouseholdAppliances',
    protocolId,
    offlineMessage: 'Dodanie sprzętu AGD zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Dodano nowy sprzęt AGD`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas dodawania sprzętu AGD. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const updateProtocolHouseholdAppliance = useOfflineMutation<
    ProtocolHouseholdAppliance,
    any,
    Partial<ProtocolHouseholdAppliance>
  >({
    endpoint: (householdAppliance: Partial<ProtocolHouseholdAppliance>) =>
      `/admin/protocol/protocols/${protocolId}/protocol-household-appliances/${householdAppliance.id}/`,
    method: 'PATCH',
    mutationType: 'protocolHouseholdAppliances',
    protocolId,
    offlineMessage: 'Aktualizacja sprzętu AGD zostanie wykonana po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Aktualizacja zakończona pomyślnie`, {
        variant: 'success'
      })
      rest.refetch()
    },
    onError: (error: any) => {
      enqueueSnackbar(`Błąd podczas aktualizacji sprzętu AGD. Spróbuj ponownie`, {
        variant: 'bug' as any,
        bugDetails: error?.response?.data ? JSON.stringify(error?.response?.data) : 'Błąd serwera'
      })
    }
  })

  const deleteProtocolHouseholdAppliance = useOfflineMutation<
    ProtocolHouseholdAppliance,
    any,
    ProtocolHouseholdAppliance['id']
  >({
    endpoint: (applianceId) =>
      `/admin/protocol/protocols/${protocolId}/protocol-household-appliances/${applianceId}/`,
    method: 'DELETE',
    mutationType: 'protocolHouseholdAppliances',
    protocolId,
    offlineMessage: 'Usunięcie sprzętu AGD zostanie wykonane po powrocie połączenia',
    onSuccess: () => {
      enqueueSnackbar(`Usunięto sprzęt AGD`, {
        variant: 'success'
      })
      rest.refetch()
    }
  })

  return {
    protocolHouseholdAppliances,
    createProtocolHouseholdAppliance,
    updateProtocolHouseholdAppliance,
    deleteProtocolHouseholdAppliance,
    ...rest
  }
}

export const useFlatHouseholdAppliances = (protocolId: Protocol['id']) => {
  const { data: flatHouseholdAppliances = [], ...rest } = useQuery({
    queryKey: ['flatHouseholdAppliances', protocolId],
    queryFn: () =>
      apiInstance<HouseholdAppliance[]>({
        url: `/admin/protocol/protocols/${protocolId}/protocol-household-appliances/flat-household-appliances/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  return { flatHouseholdAppliances, ...rest }
}

export const useWarehouseHouseholdAppliances = (protocolId: Protocol['id']) => {
  const { data: warehouseHouseholdAppliances = [], ...rest } = useQuery({
    queryKey: ['warehouseHouseholdAppliances', protocolId],
    queryFn: () =>
      apiInstance<HouseholdAppliance[]>({
        url: `/admin/protocol/protocols/${protocolId}/protocol-household-appliances/warehouse-household-appliances/`,
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true && !!protocolId
  })

  return { warehouseHouseholdAppliances, ...rest }
}

export interface HouseholdApplianceType {
  id: number
  name: string
  nameLocal: string
}

export const useHouseholdApplianceTypes = () => {
  const { data: householdApplianceTypes = [], ...rest } = useQuery({
    queryKey: ['householdApplianceTypes'],
    queryFn: () =>
      apiInstance<HouseholdApplianceType[]>({
        url: '/admin/protocol/household-appliance-types/',
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  const getHouseholdApplianceNameById = (id: number | null | string) => {
    if (id === null) {
      return 'Brak typu'
    }
    const type = householdApplianceTypes.find((type) => type.id === id)
    return type ? type.nameLocal : 'Nieznany typ'
  }

  return { householdApplianceTypes, getHouseholdApplianceNameById, ...rest }
}

export interface ResidentialHouseholdApplianceProducer {
  id: number
  name: string
}

export const useResidentialHouseholdApplianceProducers = () => {
  const { data: residentialHouseholdApplianceProducers = [], ...rest } = useQuery({
    queryKey: ['residentialHouseholdApplianceProducers'],
    queryFn: () =>
      apiInstance<ResidentialHouseholdApplianceProducer[]>({
        url: '/admin/protocol/residential-household-appliance-producers/',
        method: 'GET'
      }).then(({ data }) => data),
    enabled: true
  })

  const getHouseholdApplianceProducerNameById = (id: number | null) => {
    if (id === null) {
      return 'Brak producenta'
    }
    const producer = residentialHouseholdApplianceProducers.find((prod) => prod.id === id)
    return producer ? producer.name : 'Nieznany producent'
  }

  return { residentialHouseholdApplianceProducers, getHouseholdApplianceProducerNameById, ...rest }
}
